import React, { useState } from 'react';

export default function CalculadoraPrejuizoEBTT() {
  const [dados, setDados] = useState({
    dataInicioRecebido: '',
    dataCorreto: '',
    dataFinal: '',
    juros: 0.5,    // % ao mês
    correcao: 0.0, // % ao mês
  });

  const [resultado, setResultado] = useState(null);
  const [detalhamento, setDetalhamento] = useState([]);

  // tabelas salariais (vigências a partir do mês indicado)
  const tabelas = [
    { ano: 2017, mes: 8, D301: 11323.71, D302: 11629.58, D303: 12060.77, D304: 12512.71, D401: 15806.58, D402: 16325.34 },
    { ano: 2018, mes: 8, D301: 11561.91, D302: 11950.86, D303: 12411.89, D304: 12893.12, D401: 16199.24, D402: 16790.46 },
    { ano: 2019, mes: 8, D301: 11800.12, D302: 12272.12, D303: 12763.01, D304: 13273.52, D401: 16591.91, D402: 17255.91 },
    { ano: 2023, mes: 5, D301: 12862.13, D302: 13376.61, D303: 13911.69, D304: 14468.14, D401: 18085.19, D402: 18808.60 },
    { ano: 2025, mes: 1, D301: 14019.74, D302: 14650.62, D303: 15309.90, D304: 15998.84, D401: 19758.57, D402: 20647.71 }
  ];

  // retorna salário segundo a tabela vigente para ano/mes e nível
  function obterSalario(ano, mes, nivel) {
    const d = new Date(ano, mes - 1, 1);
    for (let i = tabelas.length - 1; i >= 0; i--) {
      const t = tabelas[i];
      const inicio = new Date(t.ano, t.mes - 1, 1);
      if (d >= inicio) return t[nivel];
    }
    return tabelas[0][nivel];
  }

  // parse safe para o primeiro dia do mês
  function parseDateToMonthStart(dateStr) {
    if (!dateStr) return null;
    const [y, m] = dateStr.split('-').map(Number);
    if (!y || !m) return null;
    return new Date(y, m - 1, 1);
  }

  // diferença em meses (start -> end). Mesmo mês => 0
  function monthsDiff(start, end) {
    return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  }

  // determina nível por número de meses desde referência
  // nivelInicialIndex: 0 => começa em D301; 1 => começa em D302
  function nivelPorMesesDesde(baseMeses, nivelInicialIndex) {
    const niveis = ['D301', 'D302', 'D303', 'D304', 'D401', 'D402'];
    const passos = Math.floor(Math.max(0, baseMeses) / 24); // cada 24 meses sobe 1 nível
    const idx = Math.min(nivelInicialIndex + passos, niveis.length - 1);
    return niveis[idx];
  }

  function calcularPrejuizo() {
    const { dataInicioRecebido, dataCorreto, dataFinal, juros, correcao } = dados;

    if (!dataInicioRecebido || !dataCorreto || !dataFinal) {
      alert('Preencha todas as datas antes de calcular.');
      return;
    }

    const baseRecebido = parseDateToMonthStart(dataInicioRecebido); // quando foi acelerado para D301
    const baseCorreto = parseDateToMonthStart(dataCorreto);         // quando deveria ter progredido para D302 (início do cálculo)
    const fim = parseDateToMonthStart(dataFinal);                   // mês final (incluído)

    if (!baseRecebido || !baseCorreto || !fim) {
      alert('Datas inválidas.');
      return;
    }
    if (baseCorreto > fim) {
      alert('A data correta de progressão deve ser anterior ou igual à data final.');
      return;
    }

    const linhas = [];
    let totalNominal = 0;
    let totalCorrigido = 0;
    const jurosMensal = Number(juros) / 100;
    const correcaoMensal = Number(correcao) / 100;

    // iterar mês a mês começando exatamente em baseCorreto (inclusivo) e incluindo 'fim'
    let current = new Date(baseCorreto.getFullYear(), baseCorreto.getMonth(), 1);
    while (current <= fim) {
      const ano = current.getFullYear();
      const mes = current.getMonth() + 1;

      const msDesdeRecebido = monthsDiff(baseRecebido, current); // pode ser negativo se aceleração após baseCorreto
      const msDesdeCorreto = monthsDiff(baseCorreto, current);   // 0 no mês baseCorreto

      // nível recebido: parte de D301 na dataInicioRecebido e progide a cada 24 meses
      const nivelRecebido = nivelPorMesesDesde(msDesdeRecebido, 0);
      // nível correto: começa em D302 na dataCorreto e progide a cada 24 meses
      const nivelCorreto = nivelPorMesesDesde(msDesdeCorreto, 1);

      const salarioRecebido = obterSalario(ano, mes, nivelRecebido);
      const salarioCorreto = obterSalario(ano, mes, nivelCorreto);

      const diffSalario = Math.max(0, salarioCorreto - salarioRecebido);

      // férias (1/3 anual) proporcional mensal -> diff / 36
      const ferias = diffSalario / 36;
      // 13º proporcional mensal -> diff / 12
      const decimo = diffSalario / 12;
      const bruto = diffSalario + ferias + decimo;

      // meses entre dataAtual e mês final -> fator de correção retroativa
      const mesesAteFim = monthsDiff(current, fim);
      const fator = Math.pow(1 + jurosMensal + correcaoMensal, mesesAteFim);
      const valorCorrigido = bruto * fator;

      totalNominal += bruto;
      totalCorrigido += valorCorrigido;

      linhas.push({
        mesAno: current.toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' }),
        nivelRecebido,
        nivelCorreto,
        salarioRecebido: salarioRecebido.toFixed(2),
        salarioCorreto: salarioCorreto.toFixed(2),
        diferenca: diffSalario.toFixed(2),
        ferias: ferias.toFixed(2),
        decimo: decimo.toFixed(2),
        bruto: bruto.toFixed(2),
        valorCorrigido: valorCorrigido.toFixed(2),
        brutoNum: bruto,
        valorCorrigidoNum: valorCorrigido,
      });

      // avançar 1 mês
      current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
    }

    // totais últimos 60 meses
    const limite = 60;
    const inicio60 = Math.max(0, linhas.length - limite);
    const ultimos60 = linhas.slice(inicio60);
    const totalNominal60 = ultimos60.reduce((s, r) => s + r.brutoNum, 0);
    const totalCorrigido60 = ultimos60.reduce((s, r) => s + r.valorCorrigidoNum, 0);

    setResultado({
      periodo: `${baseCorreto.toLocaleDateString('pt-BR')} a ${fim.toLocaleDateString('pt-BR')}`,
      meses: linhas.length,
      totalNominal: totalNominal.toFixed(2),
      totalCorrigido: totalCorrigido.toFixed(2),
      totalNominal60: totalNominal60.toFixed(2),
      totalCorrigido60: totalCorrigido60.toFixed(2),
      aviso: linhas.length > limite ? `Detalhamento completo; também calculado últimos ${limite} meses.` : ''
    });

    setDetalhamento(linhas);
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setDados({ ...dados, [name]: value });
  }

  return (
    <div className="p-6 max-w-5xl mx-auto bg-white rounded-2xl shadow-md">
      <h1 className="text-2xl font-bold mb-4 text-center">Calculadora de Perdas Salariais - Reenquadramento EBTT</h1>
      <h5 className="text-base font-bold mb-4 text-center">Desenvolvido por Adriano Santos - IFMG Ribeirão das Neves</h5>
      <h5 className="text-sm text-red-600 font-bold mb-4 text-center">Isenção de responsabilidade: Valores aproximados para o professor ter ideia do prejuízo financeiro.</h5>
      <h5 className="text-sm text-blue-600 font-bold mb-4 text-center">ADIFMG - Associação dos Docentes do IFMG</h5>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium">Data em que você foi acelerado para o nível D301:</label>
          <input type="date" name="dataInicioRecebido" value={dados.dataInicioRecebido} onChange={handleChange} className="border rounded p-2 w-full" />
        </div>
        <div>
          <label className="block text-sm font-medium">Nível recebido:</label>
          <input type="text" value="D301" disabled className="border rounded p-2 w-full bg-gray-100" />
        </div>
        <div>
          <label className="block text-sm font-medium">Data correta que deveria ter progredido para o nível D302:</label>
          <input type="date" name="dataCorreto" value={dados.dataCorreto} onChange={handleChange} className="border rounded p-2 w-full" />
        </div>
        <div>
          <label className="block text-sm font-medium">Nível correto:</label>
          <input type="text" value="D302" disabled className="border rounded p-2 w-full bg-gray-100" />
        </div>
        <div>
          <label className="block text-sm font-medium">Data final de cálculo:</label>
          <input type="date" name="dataFinal" value={dados.dataFinal} onChange={handleChange} className="border rounded p-2 w-full" />
        </div>
        <div>
          <label className="block text-sm font-medium">Juros moratórios (% ao mês):</label>
          <input type="number" name="juros" value={dados.juros} onChange={handleChange} step="0.1" className="border rounded p-2 w-full" />
        </div>
        <div>
          <label className="block text-sm font-medium">Correção monetária (% ao mês):</label>
          <input type="number" name="correcao" value={dados.correcao} onChange={handleChange} step="0.1" className="border rounded p-2 w-full" />
        </div>
      </div>

      <button onClick={calcularPrejuizo} className="mt-6 w-full bg-blue-600 text-white p-3 rounded-xl font-semibold hover:bg-blue-700 transition">Calcular Prejuízo</button>

      {resultado && (
        <div className="mt-6 p-4 bg-green-100 border border-green-300 rounded-xl">
          <p className="text-lg font-semibold">Período de cálculo: {resultado.periodo}</p>
          <p className="text-lg">Total nominal (sem juros/correção): <strong>R$ {resultado.totalNominal}</strong></p>
          <p className="text-lg">Total corrigido (juros + correção): <strong>R$ {resultado.totalCorrigido}</strong></p>
          {resultado.meses > 60 && (
            <>
              <p className="text-lg">Total últimos 60 meses (sem juros/correção): <strong>R$ {resultado.totalNominal60}</strong></p>
              <p className="text-lg">Total últimos 60 meses (com juros + correção): <strong>R$ {resultado.totalCorrigido60}</strong></p>
            </>
          )}
          {resultado.aviso && <p className="text-red-600 font-medium">{resultado.aviso}</p>}
          <p className="text-sm text-gray-600">Período total: {resultado.meses} meses</p>
        </div>
      )}

      {detalhamento.length > 0 && (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full border text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-2">Mês/Ano</th>
                <th className="border p-2">Nível Recebido</th>
                <th className="border p-2">Nível Correto</th>
                <th className="border p-2">Salário Recebido</th>
                <th className="border p-2">Salário Correto</th>
                <th className="border p-2">Diferença</th>
                <th className="border p-2">1/3 Férias</th>
                <th className="border p-2">13º Proporcional</th>
                <th className="border p-2">Total Bruto</th>
                <th className="border p-2">Com Juros + Correção</th>
              </tr>
            </thead>
            <tbody>
              {detalhamento.map((linha, idx) => (
                <tr key={idx} className="text-center">
                  <td className="border p-2">{linha.mesAno}</td>
                  <td className="border p-2">{linha.nivelRecebido}</td>
                  <td className="border p-2">{linha.nivelCorreto}</td>
                  <td className="border p-2">R$ {linha.salarioRecebido}</td>
                  <td className="border p-2">R$ {linha.salarioCorreto}</td>
                  <td className="border p-2">R$ {linha.diferenca}</td>
                  <td className="border p-2">R$ {linha.ferias}</td>
                  <td className="border p-2">R$ {linha.decimo}</td>
                  <td className="border p-2">R$ {linha.bruto}</td>
                  <td className="border p-2 font-semibold">R$ {linha.valorCorrigido}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
